import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Landmark, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { T, getLang } from "../../../lib/i18n";
import { usToday } from "../../../lib/usDate";
import { errTextOf } from "../../../lib/detailText";

/**
 * City 批准登记弹窗（2026-11-04，客户反馈 1049 Steinway Ave, Campbell）。
 *
 * 【需求背景】客户原话：「City 已批准，但系统节点无法同步…请调整：1. 将 City Approval 与
 * Permit Issued 分开记录，支持 City Approved — Pending Fees / Permit Issuance 状态。
 * 2. 允许记录已取得的批准，同时保留 School Fee 和 Permit Issuance Payment 未完成。
 * 3. 确认该状态更新不会自动完成 Third Payment + Handoff，或错误触发第三笔付款及工程交接。」
 *
 * 【为什么是「登记」而不是「把节点标完成」】见后端 design_module.stage_city_approval 的注释：
 * 批准节点同时是第三笔款触发点与移交门禁，提前关掉会立刻催收 30% 并可能放行交接。
 * 所以这个弹窗只写一条 `cityApproval` 事实记录：
 *   · 不改阶段状态 → 流程前沿、顺序门禁、逾期口径全部不变；
 *   · 不触发付款 / 不发起移交审批 → 第三笔款与 Third Payment + Handoff 的触发条件
 *     与改动前逐字一致（弹窗里也把这句话直接写给用户看，避免以为「点了就催款」）。
 *
 * 【为什么批准日期用文本框而不是纯 date 输入】项目日期一律按美国太平洋时间（见
 * lib/usDate 与后端 PROJECT_TZ），`<input type="date">` 在浏览器时区下可能回显成前一天；
 * 这里用 date 输入为主、并按 `usToday()` 初始化，用户仍可手填历史日期。
 *
 * 与后端契约：
 *   POST   /design/stages/{sid}/city-approval  {approvedAt, note, pending[]}
 *   DELETE /design/stages/{sid}/city-approval
 * pending 的 key 由后端 /design/meta 下发（cityApprovalPending），前端不另抄一份。
 */

/** 只有这两个节点承载「City 批准」；与后端 design_module.CITY_APPROVAL_STAGES 一致。 */
export const CITY_APPROVAL_STAGES = ["planning_approval", "building_approval"];

/** 阶段上的 City 批准**正式登记**记录（没有批准日期视为未登记，与后端 city_approval_of 同口径）。 */
export const cityApprovalOf = (stage) => (stage?.cityApproval?.approvedAt ? stage.cityApproval : null);

/** 展示用的 City 批准证据：正式登记优先，其次后端按「对应审图阶段已通过」推导的只读结论。
 *
 * 为什么要分两层：登记是责任主体明确的正式记录；推导只是让页面先与事实一致
 * （客户 1049 的成因就是「批了却显示未开始」，若显示要等人工登记，同类项目会长期不符）。
 * 推导不能替代登记：它没有登记人、没有尾项清单，也不能被撤销。
 */
export const cityApprovalEvidenceOf = (stage) => cityApprovalOf(stage)
  || (stage?.cityApprovalDerived?.approvedAt ? { ...stage.cityApprovalDerived, derived: true } : null);

/** 从合并节点（P+B）里找到承载 City 批准的那个成员阶段。
 *
 * 选择顺序刻意写成三级，而不是「取第一个批准阶段」：
 *   ① 已经登记过的那条 —— P+B 下可能落在 Planning 或 Building 任一侧，登记入口要能改它；
 *   ② Building 批准 —— City 批的是施工许可那一套，默认落在 Building 侧（客户场景就是这一侧）；
 *   ③ 组内第一个批准阶段（只有 Planning 批准的精简流程）。
 * 取错一侧不会丢数据，但会让人在「Planning 批准」上登记施工许可的批准，语义就不对了。
 */
export const cityApprovalStageOf = (stage) => {
  const members = (stage?.stages?.length ? stage.stages : [stage])
    .filter((m) => m && CITY_APPROVAL_STAGES.includes(m.key));
  if (!members.length) return null;
  return members.find((m) => cityApprovalOf(m))
    || members.find((m) => m.key === "building_approval")
    || members[0];
};

/** 徽标文案：City 已批准 · 待 School Fee、Permit Issuance Payment（labelMap 由 meta 下发）。 */
export const cityApprovalSummary = (ca, labelMap = {}) => {
  if (!ca) return "";
  const names = (ca.pending || []).map((k) => labelMap[k] || k);
  // 分隔符按界面语言给：中文顿号、英文/西语逗号。标点本身不走 T()：
  // 中文模式下 T() 原样返回，英文模式下会把它当待翻译文本，反而可能变成逗号。
  const sep = getLang() === "zh" ? "、" : ", ";
  if (names.length) return T("City 已批准 · 待 {0}", [names.join(sep)]);
  // 推导出来的结论没有尾项清单（没人登记），所以要写清它还是「待 Permit / 尾款」的中间态。
  return ca.derived ? T("City 已批准（按审图通过）· 待 Permit / 尾款")
    : T("City 已批准（尾项已清，节点待收口）");
};

export const CityApprovalDialog = ({
  stage,                 // 承载批准的真实阶段文档（planning_approval / building_approval）
  stageName,             // 展示用阶段名
  pendingOptions = [],   // [{key, label}] 来自 /design/meta
  defaultApprovedAt = "", // 已有 City 审图通过日时带出（避免把 10-30 的批准记成今天）
  api,
  canEdit = true,
  onClose,
  onSaved,
}) => {
  const existing = cityApprovalOf(stage) || {};
  /* 批准日默认取「City 审图通过日」而不是今天：
     客户 1049 的批准发生在 2026-10-30，隔几天才来登记，若默认今天就会把历史写成登记日；
     日期是这条记录唯一的硬事实，写错等于记录失效。没有审图通过日（或读不到）才退回今天。 */
  const [approvedAt, setApprovedAt] = useState(existing.approvedAt || defaultApprovedAt || usToday());
  const [note, setNote] = useState(existing.note || "");
  const [pending, setPending] = useState(Array.isArray(existing.pending) ? existing.pending : []);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const toggle = (key) => setPending((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(approvedAt)) {
      toast.error(T("批准日期格式必须是 YYYY-MM-DD"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/design/stages/${stage.id}/city-approval`, { approvedAt, note, pending });
      toast.success(T("已登记 City 批准：节点保持未完成，第三笔款与移交不会被触发"));
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(errTextOf(e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm(T("撤销这条 City 批准登记？\n\n只会删掉记录，阶段状态、付款与移交都不受影响。"))) return;
    setBusy(true);
    try {
      await api.delete(`/design/stages/${stage.id}/city-approval`);
      toast.success(T("已撤销 City 批准登记"));
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(errTextOf(e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4"
      onClick={() => { if (!busy) onClose?.(); }} data-testid="dv3-city-approval-overlay">
      <div role="dialog" aria-modal="true" aria-labelledby="dv3-city-approval-title"
        className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="dv3-city-approval-dialog">
        <div className="flex items-start gap-2">
          <Landmark className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" />
          <div className="min-w-0">
            <h3 id="dv3-city-approval-title" className="font-bold text-slate-900 text-sm">
              {existing.approvedAt ? T("City 批准登记") : T("登记 City 批准")}
            </h3>
            {stageName && <p className="text-xs text-slate-500 mt-0.5 truncate" data-testid="dv3-city-approval-stage">{T(stageName)}</p>}
          </div>
        </div>
        {/* 这句话是客户第 3 条要求的正面回答：状态更新不会自动完成 Third Payment + Handoff。 */}
        <p className="text-xs text-slate-600 leading-5" data-testid="dv3-city-approval-scope">
          {T("这里只记录「City 已批准」以及仍在办理的尾项。本节点仍保持未完成：单据、产物与 Permit 门禁不变，不会自动完成第三笔款 + 移交施工，也不会触发第三笔款催收。")}
        </p>
        <label className="block text-xs text-slate-600 space-y-1">
          <span className="font-semibold">{T("City 批准日期")}</span>
          <input ref={firstRef} type="date" value={approvedAt} disabled={busy || !canEdit}
            onChange={(e) => setApprovedAt(e.target.value)} data-testid="dv3-city-approval-date"
            className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" />
          {defaultApprovedAt && approvedAt !== defaultApprovedAt && (
            <button type="button" onClick={() => setApprovedAt(defaultApprovedAt)} disabled={busy || !canEdit}
              data-testid="dv3-city-approval-use-review-date" data-review-date={defaultApprovedAt}
              className="text-[10px] text-violet-700 font-semibold hover:underline text-left">
              {T("改用 City 审图通过日 {0}", [defaultApprovedAt])}
            </button>
          )}
        </label>
        <div className="space-y-1">
          <span className="text-xs font-semibold text-slate-600">{T("仍在办理 / 待缴（不影响批准已取得这一事实）")}</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {pendingOptions.map((o) => (
              <label key={o.key} className="flex items-center gap-2 text-xs text-slate-700 border border-slate-200 rounded-md px-2 py-1.5">
                <input type="checkbox" checked={pending.includes(o.key)} disabled={busy || !canEdit}
                  onChange={() => toggle(o.key)} data-testid={`dv3-city-approval-pending-${o.key}`} />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
          </div>
          {!pendingOptions.length && <span className="text-[11px] text-slate-400">{T("尾项选项未加载，可先在备注里写清待办事项")}</span>}
        </div>
        <label className="block text-xs text-slate-600 space-y-1">
          <span className="font-semibold">{T("备注")}<span className="font-normal text-slate-400">{T("（可选）")}</span></span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} disabled={busy || !canEdit}
            data-testid="dv3-city-approval-note" placeholder={T("批准函编号、City 口径、与业主沟通的实际进度等")}
            className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" />
          {defaultApprovedAt && approvedAt !== defaultApprovedAt && (
            <button type="button" onClick={() => setApprovedAt(defaultApprovedAt)} disabled={busy || !canEdit}
              data-testid="dv3-city-approval-use-review-date" data-review-date={defaultApprovedAt}
              className="text-[10px] text-violet-700 font-semibold hover:underline text-left">
              {T("改用 City 审图通过日 {0}", [defaultApprovedAt])}
            </button>
          )}
        </label>
        {existing.approvedAt && (
          <p className="text-[11px] text-slate-400" data-testid="dv3-city-approval-by">
            {T("上次登记：{0} · {1}", [existing.approvedAt, existing.by || "—"])}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1 flex-wrap">
          {existing.approvedAt && canEdit && (
            <Button size="sm" variant="outline" onClick={revoke} disabled={busy} data-testid="dv3-city-approval-revoke"
              className="border-red-300 text-red-700 hover:bg-red-50 mr-auto">{T("撤销登记")}</Button>
          )}
          <Button size="sm" variant="outline" onClick={onClose} disabled={busy} data-testid="dv3-city-approval-cancel">{T("取消")}</Button>
          {canEdit && (
            <Button size="sm" onClick={save} disabled={busy} data-testid="dv3-city-approval-save"
              className="bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : T("保存登记")}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CityApprovalDialog;
